/**
 * First-run setup — creating the very first console account.
 *
 * Shown ONLY while no account exists, and gone permanently the moment one does. Nothing has to be turned
 * off afterwards: an instruction to disable something later is a step somebody eventually skips.
 *
 * 🔴 It asks for a setup code, and the reason is worth stating plainly on the screen itself: this console
 * is on the public internet, so a setup page anyone could complete would be claimable by whoever reached
 * it first. The code is printed in the engine's own log, so using it proves server access — the same bar
 * the command-line bootstrap always had, with a far better experience.
 */
import { useState, type FormEvent } from "react";
import { api, ApiError } from "../lib/api";
import { C } from "../ui/primitives";

const Mark = () => (
  <svg viewBox="0 0 120 120" width="18" height="18" fill="none">
    <rect x="22" y="12" width="20" height="96" rx="10" fill="#F5F3EE" />
    <path fillRule="evenodd" fill="#F5F3EE" d="M28,76 a32,32 0 1,0 64,0 a32,32 0 1,0 -64,0 Z M46,76 a14,14 0 1,0 28,0 a14,14 0 1,0 -28,0 Z" />
  </svg>
);

export function Setup({ onDone }: { onDone: () => void }) {
  const [token, setToken] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password.length < 12) { setErr("The password must be at least 12 characters."); return; }
    setBusy(true); setErr(null);
    try {
      await api.setup({ token: token.trim(), email: email.trim(), name: name.trim(), password });
      setDone(true);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : "Could not complete setup");
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Frame>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>Your account is ready.</div>
          <div style={{ fontSize: 13, lineHeight: 1.6, color: "var(--ink2)" }}>
            You are the <b>super admin</b>. Sign in now — you will be asked to set up an authenticator app,
            and <b>no session is issued until you confirm a live code</b>.
            <br /><br />
            This setup page is now gone for good. Everyone else gets access from <b>Team</b> inside the
            console, where each grant is recorded against your name.
          </div>
          <button type="button" onClick={onDone}
            style={{ padding: 13, border: "none", borderRadius: 11, background: C.accent, color: "#fff", fontSize: 13.5, fontWeight: 800, cursor: "pointer" }}>
            Go to sign in
          </button>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <div>
        <div style={{ fontWeight: 800, fontSize: 16 }}>Set up the Payment Manager</div>
        <div style={{ fontSize: 12.5, color: "var(--ink2)", lineHeight: 1.55, marginTop: 4 }}>
          No account exists yet. This creates the first one — a <b>super admin</b> — and then this page
          disappears permanently.
        </div>
      </div>

      <div style={{ padding: "11px 13px", border: "1px solid var(--line2)", background: "var(--accentsoft)", borderRadius: 11, fontSize: 12, lineHeight: 1.55, color: "var(--ink2)" }}>
        <b>Where the setup code comes from.</b> The engine prints it in its own log at startup:
        <div className="bp-mono" style={{ marginTop: 6, padding: "7px 9px", background: "var(--subtle)", border: "1px solid var(--line2)", borderRadius: 8, fontSize: 11.5, overflowX: "auto", whiteSpace: "nowrap" }}>
          sudo -iu deploy pm2 logs bluepilo-payments --lines 60 --nostream
        </div>
        <div style={{ marginTop: 6 }}>
          It is required because this console is reachable from the internet — without it, whoever found this
          page first could claim the console. The code proves you have server access.
        </div>
      </div>

      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <label style={lbl}><span style={lblText}>Setup code</span>
          <input value={token} onChange={(e) => setToken(e.target.value)} required autoFocus
            className="bp-mono" placeholder="from the engine log" style={fld} /></label>
        <label style={lbl}><span style={lblText}>Your work email</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required autoComplete="username" placeholder="you@bluepilo.com" style={fld} /></label>
        <label style={lbl}><span style={lblText}>Your full name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Ada Okafor" style={fld} /></label>
        <label style={lbl}><span style={lblText}>Password — 12 characters or more</span>
          <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required autoComplete="new-password" placeholder="••••••••••••" style={fld} /></label>

        {err && (
          <div style={{ padding: "11px 13px", border: `1px solid ${C.dangerLine}`, background: C.dangerSoft, borderRadius: 11, fontSize: 12.5, lineHeight: 1.5, color: "var(--ink2)" }}>
            {err}
          </div>
        )}

        <button type="submit" disabled={busy}
          style={{ padding: 13, border: "none", borderRadius: 11, background: busy ? "var(--muted)" : C.accent, color: "#fff", fontSize: 13.5, fontWeight: 800, cursor: busy ? "wait" : "pointer" }}>
          {busy ? "Creating…" : "Create the super admin account"}
        </button>
      </form>
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "26px 18px" }}>
      <div style={{ width: "100%", maxWidth: 470, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 18, padding: "26px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: "#1C2030", display: "flex", alignItems: "center", justifyContent: "center" }}><Mark /></div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>Payment Manager</div>
            <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700 }}>Bluepilo staff console</div>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

const lbl: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 5 };
const lblText: React.CSSProperties = { fontSize: 12, fontWeight: 700 };
const fld: React.CSSProperties = { padding: "12px 13px", border: "1px solid var(--line2)", borderRadius: 10, background: "var(--subtle)", color: "var(--ink)", fontSize: 14 };
